import { useEffect, useState } from 'react';
import { FileQuestion, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useSuppliers } from '@/features/procurement/hooks/useProcurementApi';
import { createRfq, getRequisition, listRequisitions } from '../api/procurementRfqApi';
import { formatMinor } from '../constants';
import type { PurchaseRequisition, Rfq } from '../types';

/**
 * Raise an RFQ, normally from an approved purchase requisition.
 *
 * The requisition is the entry point because that is how an RFQ actually
 * starts: somebody asked for material, and buying now needs rates for it. The
 * lines are NOT sent from here — POST /api/accounting/rfq copies them from the
 * requisition server-side, deliberately as a snapshot, so a later edit to the
 * PR cannot change what suppliers were asked to quote against. This modal only
 * previews them so the buyer can see what they are about to send out.
 *
 * Suppliers come from useSuppliers() (/api/procurement/suppliers), the existing
 * procurement-scoped supplier lookup the V1 PO form already uses, rather than a
 * new vendor fetch.
 */
export function RaiseRfqModal({
  open,
  initialRequisitionId,
  onClose,
  onCreated,
}: {
  open: boolean;
  initialRequisitionId?: string | null;
  onClose: () => void;
  onCreated: (rfq: Rfq) => void;
}) {
  const [requisitions, setRequisitions] = useState<PurchaseRequisition[]>([]);
  const [requisitionId, setRequisitionId] = useState('');
  const [preview, setPreview] = useState<PurchaseRequisition | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [form, setForm] = useState({ title: '', dueDate: '', notes: '' });
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const { data: suppliers = [], isLoading: suppliersLoading } = useSuppliers(open);

  // Requisitions worth quoting are the approved ones. Submitted ones are listed
  // too — the server accepts any requisition — but approved is what a buyer
  // normally acts on, so it leads.
  useEffect(() => {
    if (!open) return;
    setRequisitionId(initialRequisitionId || '');
    setForm({ title: '', dueDate: '', notes: '' });
    setSupplierIds([]);
    listRequisitions()
      .then(setRequisitions)
      .catch(() => setRequisitions([]));
  }, [open, initialRequisitionId]);

  useEffect(() => {
    if (!open || !requisitionId) { setPreview(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    getRequisition(requisitionId)
      .then(pr => { if (!cancelled) setPreview(pr); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [open, requisitionId]);

  const toggleSupplier = (id: string) =>
    setSupplierIds(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requisitionId) { toast.error('Choose the requisition this RFQ is being raised for.'); return; }
    if (supplierIds.length === 0) { toast.error('Add at least one supplier to ask for a quote.'); return; }
    setSaving(true);
    try {
      const rfq = await createRfq({
        title: form.title,
        requisitionId,
        dueDate: form.dueDate || undefined,
        notes: form.notes,
        suppliers: supplierIds.map(id => ({
          supplierId: id,
          supplierName: suppliers.find(s => s.id === id)?.name || '',
        })),
      });
      toast.success(`${rfq.rfqNumber || 'RFQ'} raised.`);
      onCreated(rfq);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not raise this RFQ.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';
  const previewLines = preview?.lines || [];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-indigo-600">
          <div className="flex items-center gap-2 text-white">
            <FileQuestion className="w-5 h-5" />
            <h2 className="text-lg font-bold">Raise Request for Quotation</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">From Purchase Requisition *</label>
            <select className={inputCls} value={requisitionId} onChange={e => setRequisitionId(e.target.value)}>
              <option value="">Select a requisition...</option>
              {requisitions.map(pr => (
                <option key={pr._id} value={pr._id}>
                  {pr.prNumber || pr._id.slice(-8)} — {pr.department || pr.departmentId || 'No department'} ({pr.status})
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              The requisition's lines are copied onto the RFQ as a snapshot, so editing the PR later will not change what suppliers were asked to quote.
            </p>
          </div>

          {/* Line preview — read-only, purely so the buyer sees what goes out. */}
          {previewLoading && (
            <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading requisition lines...</div>
          )}
          {!previewLoading && previewLines.length > 0 && (
            <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>{['Item', 'Category', 'Qty', 'Expected Rate'].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-500">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {previewLines.map((line, i) => (
                    <tr key={i} className="bg-white dark:bg-slate-900">
                      <td className="px-3 py-2">
                        <div className="text-slate-700 dark:text-slate-200">{line.description || 'Item'}</div>
                        {line.specification && <div className="text-xs text-slate-400">{line.specification}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{line.materialCategory || '—'}</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {line.quantity ?? line.qty ?? 0} {line.quantityUnit || ''}
                      </td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                        {line.expectedRateMinor ? formatMinor(line.expectedRateMinor) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Title</label><input className={inputCls} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Base oil SN-500 — Q3 restock" /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Quotes Due By</label><input type="date" className={inputCls} value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} /></div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">
              Ask these suppliers to quote * {supplierIds.length > 0 && <span className="text-indigo-600">({supplierIds.length} selected)</span>}
            </label>
            {suppliersLoading ? (
              <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading suppliers...</div>
            ) : suppliers.length === 0 ? (
              <div className="text-xs text-slate-400 py-3">No suppliers found. Add a supplier before raising an RFQ.</div>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                {suppliers.map(supplier => (
                  <label key={supplier.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer">
                    <input type="checkbox" checked={supplierIds.includes(supplier.id)} onChange={() => toggleSupplier(supplier.id)} className="rounded border-slate-300" />
                    <span className="text-sm text-slate-700 dark:text-slate-200">{supplier.name}</span>
                    {supplier.gstNumber && <span className="text-xs text-slate-400 font-mono">{supplier.gstNumber}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Notes to suppliers</label><textarea rows={2} className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}Raise RFQ
          </button>
        </div>
      </form>
    </div>
  );
}

export default RaiseRfqModal;
